import {
  AssetClassFilter,
  DealRegistryPageListDueDiligenceFunnelsDistinctStaticIdQuery,
  DealRegistryPageListDueDiligenceRecordsQuery,
  DueDiligenceRecordsListSortBy,
  DueDiligenceRecordsSortDirection,
  DueDiligenceRecordStatus,
  Security_Status
} from '@/__generated__/graphql';
import { AddButtonIcon } from '@/assets/static/icons/add-button-icon';
import Button from '@/components/basicComponents/button';
import { Loader } from '@/components/loaders/loader';
import { useResponsive } from '@/hooks/use-responsive';
import { useSearchParamsManager } from '@/hooks/use-search-params';
import { MainWrap, PaddingWrap } from '@/styles/common';
import { NetworkStatus, useMutation, useQuery } from '@apollo/client';
import { debounce } from 'lodash';
import { useEffect, useState } from 'react';
import { NavigateOptions, useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { createSimpleSelectOption } from '../basicComponents/select/utils';
import { UPDATE_INVESTMENT_MUTATION, UPDATE_RECORD_STATUS_MUTATION } from '../dueDiligence-page/queries';
import Header from '../header';
import { initialValuesFromTable } from '../table/constants';
import FilterPage from '../table/filterPage';
import { AdditionalFilter, FiltersItems } from '../table/filterPage/types';
import { createNewFilter } from '../table/filterPage/utils';
import { TableColumns } from '../table/types';
import { dealRegistrySort, dealRegistryStatusFilterSelectOptions, dealRegistryTableColumns } from './constants';
import AiSuggestions from './modals/aiSuggestions';
import DeleteDeal from './modals/deleteDeal';
import StartDueDiligence from './modals/startDueDiligence';
import {
  DELETE_DUE_DILIGENCE_RECORD_MUTATION,
  LIST_DUE_DILIGENCE_FUNNELS_DISTINCT_STATIC_ID_QUERY,
  LIST_DUE_DILIGENCE_RECORD_QUERY
} from './queries';
import { DealRegistryTable } from './table/table';
import DealRegistryMobileTable from './table/tableMobile';
import { ASSET_CLASS, DealRegistryFiltersStructure } from './types';
import { dealRegistryFilterStatus } from './utils';

export const DealRegistry = () => {
  const navigate = useNavigate();
  const { isMobile } = useResponsive();
  const { searchParams, setSearchParams, onChangeSearch, onChangePrimaryFilter, saveScrollPostion, makeScroll } = useSearchParamsManager();

  const q = searchParams.get('q') || '';
  const key = (searchParams.get('key') as DueDiligenceRecordsListSortBy) || DueDiligenceRecordsListSortBy.CreatedDate;
  const asc = searchParams.get('asc') === 'true' || false;
  const primaryFilter = searchParams.get('primaryFilter') || 'All Statuses';
  const assetClassParams = searchParams.get('assetClass') || '';
  const funnelParams = searchParams.get('funnel') || '';
  const position = searchParams.get('position') || 0;

  const [dealRegistryRows, setDealRegistryRows] = useState<DealRegistryPageListDueDiligenceRecordsQuery['listDueDiligenceRecords']['records']>([]);
  const [isModalWindowOpen, setIsModalWindowOpen] = useState({ isOpen: false, type: 'edit-table' });
  const [windowType, setWindowType] = useState({ isOpen: false, typeWindow: 'default' });
  const [columnsList, setColumnsList] = useState<TableColumns[]>(dealRegistryTableColumns);
  const [currentRow, setCurrentRow] = useState<DealRegistryPageListDueDiligenceRecordsQuery['listDueDiligenceRecords']['records'][number] | null>(
    null
  );
  const [funnelList, setFunnelList] = useState<string[]>([]);
  const [additionalFilters, setAdditionalFilters] = useState<AdditionalFilter[]>(() => getAdditionalFilters());
  const [filtersItems, setFiltersItems] = useState<FiltersItems>({
    assetClass: [createSimpleSelectOption('All Asset Classes'), ...Object.values(ASSET_CLASS).map((item) => createSimpleSelectOption(item))],
    funnel: [createSimpleSelectOption('All Funnels')]
  });
  const [mobileSort, setMobileSort] = useState(dealRegistrySort[2].title);
  const sortItem = dealRegistrySort.find((item) => item.title === mobileSort);
  const mobileSortList = dealRegistrySort.map((item) => item.title);
  const [selectedFunnelsFilter, setSelectedFunnelsFilter] = useState<string[]>([]);
  const [paginationData, setPaginationData] = useState({ offset: 0, limit: initialValuesFromTable.initialAmountRowsPerPage[0] });
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [hasMoreData, setHasMoreData] = useState(true);
  const [total, setTotal] = useState(0);

  const [deleteRecord, { loading: deleteRecordLoading }] = useMutation(DELETE_DUE_DILIGENCE_RECORD_MUTATION);
  const [updateRecordStatus, { loading: updateRecordStatusLoading }] = useMutation(UPDATE_RECORD_STATUS_MUTATION);
  const [updateInvestment, { loading: updateInvestmentStatusLoading }] = useMutation(UPDATE_INVESTMENT_MUTATION);

  const unitedFilters = additionalFilters.reduce<DealRegistryFiltersStructure>(
    (acc, { selected, typeFilter }) => {
      return acc[typeFilter as keyof DealRegistryFiltersStructure]
        ? {
            ...acc,
            [typeFilter]: [...acc[typeFilter as keyof DealRegistryFiltersStructure], selected]
          }
        : acc;
    },
    {
      assetClass: [],
      funnel: []
    }
  );

  const variables = {
    sortBy: isMobile ? sortItem?.key : key,
    sortDirection: isMobile
      ? sortItem?.asc
        ? DueDiligenceRecordsSortDirection.Asc
        : DueDiligenceRecordsSortDirection.Desc
      : asc
        ? DueDiligenceRecordsSortDirection.Asc
        : DueDiligenceRecordsSortDirection.Desc,
    statusFilter: dealRegistryFilterStatus(primaryFilter),
    assetClassFilter: unitedFilters.assetClass.map((item) => item.toUpperCase().replace(/\s/g, '_')) as AssetClassFilter[],
    funnelStaticIdFilter: selectedFunnelsFilter,
    search: q.toLowerCase(),
    offset: 0,
    limit: paginationData.limit
  };

  function getAdditionalFilters() {
    const matchedAdditionalFilters = [];

    if (!assetClassParams && !funnelParams) {
      return [createNewFilter('Select A Filter Option')];
    }

    if (assetClassParams) {
      for (const filterItem of JSON.parse(assetClassParams)) {
        matchedAdditionalFilters.push({ ...createNewFilter('assetClass', filterItem), listOfItems: Object.values(ASSET_CLASS) });
      }
    }
    if (funnelParams) {
      for (const filterItem of JSON.parse(funnelParams)) {
        matchedAdditionalFilters.push(createNewFilter('funnel', filterItem));
      }
    }

    return matchedAdditionalFilters;
  }

  useEffect(() => {
    if (!isMobile || window.innerHeight > 890) {
      setWindowType({ ...windowType, typeWindow: 'default' });
    }
  }, [isMobile, window.innerHeight]);

  useEffect(() => {
    setFiltersItems({
      ...filtersItems,
      funnel: [createSimpleSelectOption('All Funnels'), ...funnelList.map((item) => createSimpleSelectOption(item))]
    });
  }, [funnelList]);

  useEffect(() => {
    const newColumnsList = localStorage.getItem('dealRegistryColumnsOrder');
    if (newColumnsList) {
      const parsedColumnsList = JSON.parse(newColumnsList);

      const updatedColumns: TableColumns[] = parsedColumnsList.map((storedColumn: { [key: string]: string | boolean }) => {
        const matchingStoredColumn = dealRegistryTableColumns.find(
          (column) => column.cellKey === storedColumn.cellKey || column.cellKey === storedColumn.rowKey
        );
        if (matchingStoredColumn) {
          return { ...matchingStoredColumn, isHidden: storedColumn.isHidden };
        }
      });

      const isFixedColumnExists = updatedColumns.some((column: TableColumns) => column.cellKey === 'name');

      if (isFixedColumnExists) {
        setColumnsList(updatedColumns);
      } else {
        const fixedDealRegistryTableColumns = dealRegistryTableColumns.filter((column) => column.isFixed);
        setColumnsList([...fixedDealRegistryTableColumns, ...updatedColumns]);
      }
    }
  }, []);

  const updateRecordsActiveTierNames = (records: DealRegistryPageListDueDiligenceRecordsQuery['listDueDiligenceRecords']['records'][number][]) => {
    return records.map((record: DealRegistryPageListDueDiligenceRecordsQuery['listDueDiligenceRecords']['records'][number]) => {
      const newActiveTier = record.activeTier.funnel.latestFunnel.tiers.find((tier) => {
        if (tier.order === record.activeTier.order) {
          return tier;
        }
      });

      if (newActiveTier) {
        return {
          ...record,
          activeTier: { ...record.activeTier, name: newActiveTier.name }
        };
      }
      return record;
    });
  };

  const handleInfiniteScroll = debounce(async () => {
    if (isLoadingMore || !hasMoreData || dealRegistryRows?.length === total) return;

    const isNearBottom = Math.ceil(window.innerHeight + window.scrollY) >= document.documentElement.scrollHeight - 200;

    if (!isNearBottom) return;

    try {
      setIsLoadingMore(true);

      const newOffset = paginationData.offset + paginationData.limit;

      const { data } = await dealRegistryRefetch({
        data: { ...variables, offset: newOffset }
      });

      const newRecords = data.listDueDiligenceRecords.records;

      if (!newRecords || newRecords.length === 0) {
        setHasMoreData(false);
        return;
      }

      const updatedRecords = updateRecordsActiveTierNames(newRecords);

      setPaginationData((prev) => ({ ...prev, offset: newOffset }));
      setDealRegistryRows((prevDealRegistryRows) => {
        const existingIds = new Set(prevDealRegistryRows.map((record) => record.id));
        const uniqueNewRecords = updatedRecords.filter((record) => !existingIds.has(record.id));

        return [...prevDealRegistryRows, ...uniqueNewRecords];
      });

      if (newRecords.length < paginationData.limit) {
        setHasMoreData(false);
      }
    } catch (error) {
      console.error(error);
    } finally {
      setIsLoadingMore(false);
    }
  }, 100);

  useEffect(() => {
    setHasMoreData(true);
    setIsLoadingMore(false);
    setPaginationData((prev) => ({ ...prev, offset: 0 }));
  }, [q, key, asc, primaryFilter, assetClassParams, funnelParams, selectedFunnelsFilter, isMobile]);

  useEffect(() => {
    window.addEventListener('scroll', handleInfiniteScroll);

    return () => {
      window.removeEventListener('scroll', handleInfiniteScroll);
    };
  }, [handleInfiniteScroll]);

  const { data: funnelListData } = useQuery(LIST_DUE_DILIGENCE_FUNNELS_DISTINCT_STATIC_ID_QUERY, {
    notifyOnNetworkStatusChange: true,
    fetchPolicy: 'network-only',
    onCompleted: (data) => {
      setFunnelList(
        data.listDueDiligenceFunnelsDistinctStaticId.map(
          (funnel: DealRegistryPageListDueDiligenceFunnelsDistinctStaticIdQuery['listDueDiligenceFunnelsDistinctStaticId'][number]) => funnel.name
        )
      );
    }
  });

  useEffect(() => {
    if (funnelListData) {
      const selectedFunnels = unitedFilters.funnel
        .map((funnelName) => {
          const foundFunnel = funnelListData.listDueDiligenceFunnelsDistinctStaticId.find(
            (funnel: DealRegistryPageListDueDiligenceFunnelsDistinctStaticIdQuery['listDueDiligenceFunnelsDistinctStaticId'][number]) => {
              return funnelName === funnel.name;
            }
          );

          if (foundFunnel) {
            return foundFunnel.funnelStaticId;
          }
          return null;
        })
        .filter((funnelStaticId) => funnelStaticId !== null);

      setSelectedFunnelsFilter(selectedFunnels);
    }
  }, [additionalFilters, funnelListData]);

  const {
    loading: dealRegistryLoading,
    refetch: dealRegistryRefetch,
    networkStatus: dealRegistryNetworkStatus
  } = useQuery(LIST_DUE_DILIGENCE_RECORD_QUERY, {
    notifyOnNetworkStatusChange: true,
    fetchPolicy: 'network-only',
    variables: {
      data: {
        ...variables,
        offset: 0
      }
    },
    onCompleted: (data) => {
      const { records, total } = data.listDueDiligenceRecords;
      if (!isLoadingMore) {
        const updatedRecords = updateRecordsActiveTierNames(records);
        setDealRegistryRows(updatedRecords);
      }

      makeScroll(position);
      setTotal(total);
    }
  });

  const handleUpdateInvestmentStatus = (
    row: DealRegistryPageListDueDiligenceRecordsQuery['listDueDiligenceRecords']['records'][number],
    status: Security_Status
  ) => {
    setCurrentRow(row);
    updateInvestment({
      variables: {
        data: {
          id: row.investment.id,
          securityStatus: status
        }
      },
      onCompleted: () => {
        setDealRegistryRows((prevDealRegistryRow) =>
          prevDealRegistryRow.map((item) => {
            if (row.id === item.id) {
              return { ...item, investment: { ...item.investment, securityStatus: status } };
            }
            return item;
          })
        );
        setCurrentRow(null);
      }
    });
  };

  const startDueDiligence = (
    row: DealRegistryPageListDueDiligenceRecordsQuery['listDueDiligenceRecords']['records'][number],
    status: DueDiligenceRecordStatus
  ) => {
    setCurrentRow(row);
    updateRecordStatus({
      variables: {
        data: {
          id: row.id,
          status: status
        }
      },
      onCompleted: () => {
        setDealRegistryRows((prevDealRegistryRow) =>
          prevDealRegistryRow.map((item) => {
            if (row.id === item.id) {
              return { ...item, status: status };
            }
            return item;
          })
        );
        setCurrentRow(null);
        openPage(`/dueDiligence/${row.id}?backToTitle=Deal Registry`);
      }
    });
  };

  const handleDeleteDeal = (id: string) => {
    deleteRecord({
      variables: {
        id
      },
      onCompleted: () => {
        setDealRegistryRows((prevDealRegistryRow) => prevDealRegistryRow.filter((item) => item.id !== id));
      }
    });
  };

  const openPage = (path: string, options?: NavigateOptions) => {
    saveScrollPostion();
    navigate(path, options);
  };

  const openModalWindow = (row: DealRegistryPageListDueDiligenceRecordsQuery['listDueDiligenceRecords']['records'][number], type: string) => {
    setIsModalWindowOpen({ isOpen: true, type: type });
    setCurrentRow(row);
  };

  const openWindow = (type: string) => {
    setWindowType({ ...windowType, typeWindow: type });
  };

  const onChangeAdditionalFilters = (filters: AdditionalFilter[]) => {
    setAdditionalFilters(filters);

    const matchedAdditionalFilters = filters.reduce<DealRegistryFiltersStructure>(
      (acc, { selected, operator, typeFilter }) => {
        return acc[typeFilter as keyof DealRegistryFiltersStructure]
          ? {
              ...acc,
              [typeFilter]: [...acc[typeFilter as keyof DealRegistryFiltersStructure], selected]
            }
          : acc;
      },
      {
        assetClass: [],
        funnel: []
      }
    );

    const { assetClass, funnel } = matchedAdditionalFilters;

    setSearchParams(
      (prev) => {
        if (!assetClass.length) {
          prev.delete('assetClass');
          return prev;
        }
        prev.set('assetClass', JSON.stringify(assetClass));
        return prev;
      },
      { replace: true }
    );

    setSearchParams(
      (prev) => {
        if (!funnel.length) {
          prev.delete('funnel');
          return prev;
        }

        prev.set('funnel', JSON.stringify(funnel));
        return prev;
      },
      { replace: true }
    );
  };

  const handleDealRegistryRefetch = () => {
    setHasMoreData(true);
    setIsLoadingMore(false);
    setPaginationData((prev) => ({ ...prev, offset: 0 }));
    dealRegistryRefetch({
      data: { ...variables, offset: 0 }
    });
  };

  const tableLoading =
    !isLoadingMore &&
    (dealRegistryNetworkStatus === NetworkStatus.loading ||
      dealRegistryNetworkStatus === NetworkStatus.setVariables ||
      dealRegistryNetworkStatus === NetworkStatus.refetch);

  return (
    <>
      {isModalWindowOpen.isOpen && isModalWindowOpen.type === 'delete-deal' && (
        <DeleteDeal
          onClose={() => setIsModalWindowOpen({ type: 'delete-deal', isOpen: false })}
          isOpen={isModalWindowOpen.isOpen}
          handleDeleteDeal={handleDeleteDeal}
          row={currentRow}
        />
      )}
      {isModalWindowOpen.isOpen && isModalWindowOpen.type === 'start-diligence' && (
        <StartDueDiligence
          onClose={() => setIsModalWindowOpen({ type: 'start-diligence', isOpen: false })}
          isOpen={isModalWindowOpen.isOpen}
          startDueDiligence={startDueDiligence}
          currentRow={currentRow}
        />
      )}
      {isModalWindowOpen.isOpen && isModalWindowOpen.type === 'ai-suggestions' && (
        <AiSuggestions
          isOpen={isModalWindowOpen.isOpen}
          currentRow={currentRow}
          onClose={() => setIsModalWindowOpen({ type: 'ai-suggestions', isOpen: false })}
        />
      )}
      {/* {isModalWindowOpen.isOpen && isModalWindowOpen.type === 'edit-table' && !isMobile && window.innerHeight > 890 && (
        <EditTableSettings
          isOpen={isModalWindowOpen.isOpen}
          allColumns={dealRegistryTableColumns}
          columnsList={columnsList}
          tableName="dealRegistry"
          setColumnsList={setColumnsList}
          onClose={() => setIsModalWindowOpen({ type: 'edit-table', isOpen: false })}
        />
      )}
      {windowType.typeWindow === 'column-control' && isMobile && (
        <EditTableSettingsMobile
          columnsList={columnsList}
          setColumnsList={setColumnsList}
          onClose={() => setWindowType({ ...windowType, typeWindow: 'default' })}
        />
      )}
      {windowType.typeWindow === 'column-control' && !isMobile && window.innerHeight < 890 && (
        <EditTableSettingsPage
          columnsList={columnsList}
          setColumnsList={setColumnsList}
          onClose={() => setWindowType({ ...windowType, typeWindow: 'default' })}
        />
      )} */}
      {windowType.typeWindow === 'default' && (
        <>
          <MainWrap>
            <Header />
          </MainWrap>
          <MainWrap>
            <PaddingWrap>
              <AddButtonWrapper>
                <Button
                  size="md"
                  onClick={() => openPage(`/dueDiligence/modify/?status=${Security_Status.Lead}&backToTitle=Deal Registry`)}
                  icon={<AddButtonIcon />}
                >
                  Add New Deal
                </Button>
              </AddButtonWrapper>
              <FilterPage
                search={{
                  value: q,
                  onChange: onChangeSearch,
                  placeholder: 'Search for a deal'
                }}
                additionalFilters={{
                  filtersItems,
                  filters: additionalFilters,
                  applyFilters: onChangeAdditionalFilters,
                  amountResults: 0,
                  showChosenFilters: additionalFilters[0].typeFilter !== 'Select A Filter Option',
                  onRefetch: handleDealRegistryRefetch
                }}
                isDealRegistryFilter
                quickFilter={{
                  title: 'Status',
                  defaultValue: 'All Statuses',
                  value: primaryFilter,
                  options: dealRegistryStatusFilterSelectOptions,
                  onChange: onChangePrimaryFilter
                }}
                resultsValue={total}
                mobileSort={{
                  value: mobileSort,
                  onChange: setMobileSort,
                  selects: mobileSortList
                }}
                handleTableColumns={
                  isMobile || window.innerHeight < 890
                    ? () => openWindow('column-control')
                    : () =>
                        setIsModalWindowOpen({
                          type: 'edit-table',
                          isOpen: !isModalWindowOpen.isOpen
                        })
                }
                useNewFilters
              />
              {!isMobile ? (
                <DealRegistryTable
                  columnsList={columnsList}
                  dealRegistryRows={dealRegistryRows}
                  loading={tableLoading}
                  flashLoading={dealRegistryLoading || updateInvestmentStatusLoading || deleteRecordLoading}
                  currentRow={currentRow}
                  openPage={openPage}
                  openModalWindow={openModalWindow}
                  handleUpdateInvestmentStatus={handleUpdateInvestmentStatus}
                />
              ) : (
                <>
                  {tableLoading ? (
                    <Loader />
                  ) : (
                    <DealRegistryMobileTable
                      columnsList={columnsList}
                      dealRegistryRows={dealRegistryRows}
                      openPage={openPage}
                      openModalWindow={openModalWindow}
                      handleUpdateInvestmentStatus={handleUpdateInvestmentStatus}
                    />
                  )}
                </>
              )}
              {isLoadingMore && <Loader />}
            </PaddingWrap>
          </MainWrap>
        </>
      )}
    </>
  );
};

const AddButtonWrapper = styled.div`
  margin-top: 20px;
`;
